import {
  Alerter,
  Button,
  Card,
  CardLevel,
  Col,
  Drawer,
  FormGroup,
  Frow,
  Input,
  ISelectOption,
  NumericInput,
  PaddingLevel,
  RadioSelect,
  Select,
} from '@pinpointhq/thumbtack';
import * as React from 'react';
import { Job } from '../../../../../../../javascript/models';
import { createToast } from '../../../../../../FlashToaster';
import { DrawerHeader } from '../../../../../../shared/DrawerHeader';

interface IEditDrawer {
  job: Job;
  workstreamOptions: ISelectOption[];
  regionOptions: ISelectOption[];
  fetchData: () => void;
  handleClose: () => void;
}

export function EditDrawer({ handleClose, fetchData, job, regionOptions, workstreamOptions }: IEditDrawer) {
  const [title, setTitle] = React.useState<string>(job.title);
  const [headcount, setHeadcount] = React.useState<number>(job.headcount);
  const [selectedRegion, setSelectedRegion] = React.useState<string>(job.region);
  const [selectedWorkstream, setSelectedWorkstream] = React.useState<string>(job.workstream);
  const [removeExistingCandidates, setRemoveExistingCandidates] = React.useState<string>('false');
  const [duration, setDuration] = React.useState(job.duration);
  const [remoteOption, setRemoteOption] = React.useState(job.remoteOption);

  const [longitude, setLongitude] = React.useState(job.long);
  const [latitude, setLatitude] = React.useState(job.lat);

  const handleJobTitle = (event) => setTitle(event.target.value);
  const handleHeadcountChange = (value) => setHeadcount(value);
  const handleRegionSelect = (selectedOption: ISelectOption) => setSelectedRegion(selectedOption.value);
  const handleWorkstreamSelect = (selectedOption: ISelectOption) => setSelectedWorkstream(selectedOption.value);

  const selectedRegionOption = regionOptions.find((option) => option.value === selectedRegion);
  const selectedWorkstreamOption = workstreamOptions.find((option) => option.value === selectedWorkstream);

  const handleLongitudeChange = (event) => setLongitude(event.target.value);
  const handleLatitudeChange = (event) => setLatitude(event.target.value);
  const handleDurationChange = (selectedItem) => setDuration(selectedItem.value);
  const handleRemoteOptionChange = (selectedItem) => setRemoteOption(selectedItem.value);

  const radios = [
    { value: 'false', label: 'No' },
    { value: 'true', label: 'Yes' },
  ].map((applicationOption) => {
    const handleChange = (selectedItem) => {
      if (selectedItem.currentTarget.checked) setRemoveExistingCandidates(applicationOption.value);
    };

    return (
      <div className="col-flex-grow-1" key={applicationOption.label}>
        <RadioSelect
          label={applicationOption.label}
          onChange={handleChange}
          checked={applicationOption.value === removeExistingCandidates}
        />
      </div>
    );
  });

  const key = 'job-update';

  const handleSubmit = () => {
    Alerter.create({
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes',
      onConfirm: () => {
        createToast({ type: 'success', isLoading: true, text: 'Updating workstream', key });
        job.workstream = selectedWorkstream;
        job.long = longitude;
        job.lat = latitude;
        job.title = title;
        job.headcount = headcount;
        job.removeExistingCandidates = removeExistingCandidates === 'true';
        job.remoteOption = remoteOption;
        job.duration = duration;
        job.save().then((success) => {
          if (success) {
            createToast({ type: 'success', text: 'Successfully updated job', key });
            fetchData();
          } else {
            createToast({ type: 'error', text: 'Unable to update job', key });
          }
        });
      },
      text: "Are you sure you want to update this job? We won't make any changes to the existing slate",
    });
  };

  const disabled = !selectedWorkstream || !latitude || !longitude || !title || !headcount;

  const remoteOptions = [
    { label: 'Fully Remote', value: 'Fully Remote' },
    { label: 'Hybrid', value: 'Hybrid' },
    { label: 'In the Office', value: 'In the Office' },
  ];

  const durationOptions = [
    { label: '6 Weeks', value: '6 Weeks' },
    { label: '8 to 12 Weeks (2 to 3 months)', value: '8 to 12 Weeks (2 to 3 months)' },
    { label: '24 to 36 Weeks (6 to 9 months)', value: '24 to 36 Weeks (6 to 9 months)' },
    { label: '36 to 48 Weeks (9 to 12 months)', value: '36 to 48 Weeks (9 to 12 months)' },
  ];

  return (
    <Drawer isOpen={true} maxWidth={600}>
      <DrawerHeader title="Edit Job" handleClose={handleClose} />
      <Card paddingLevel={PaddingLevel.EXPANDED} level={CardLevel.NONE} style={{ overflowY: 'scroll' }}>
        <FormGroup label="Title">
          <input type="text" value={title} onChange={handleJobTitle} />
        </FormGroup>
        <FormGroup label="Headcount">
          <NumericInput value={headcount} onValueChange={handleHeadcountChange} fill={true} />
        </FormGroup>
        {/* <FormGroup label="New region">
          <Select
            onItemSelect={handleRegionSelect}
            items={regionOptions}
            fill={true}
            buttonProps={{ large: true }}
            activeItem={selectedRegionOption}
          />
        </FormGroup> */}
        <Frow gutters={8}>
          <Col sm={{ width: '1-2' }}>
            <FormGroup label="Latitude">
              <Input value={latitude} onChange={handleLatitudeChange} />
            </FormGroup>
          </Col>
          <Col sm={{ width: '1-2' }}>
            <FormGroup label="Longitude">
              <Input value={longitude} onChange={handleLongitudeChange} />
            </FormGroup>
          </Col>
        </Frow>
        <FormGroup label="New workstream">
          <Select
            onItemSelect={handleWorkstreamSelect}
            items={workstreamOptions}
            fill={true}
            buttonProps={{ large: true }}
            activeItem={selectedWorkstreamOption}
          />
        </FormGroup>
        <FormGroup label="Remove existing candidates" helperText="Do you want to remove the existing slate?">
          <Frow flexDirection="row" flexWrap="nowrap" justifyContent="center">
            {radios}
          </Frow>
        </FormGroup>
        <FormGroup label="Workplace type">
          <Select
            items={remoteOptions}
            onItemSelect={handleRemoteOptionChange}
            fill={true}
            buttonProps={{ large: true }}
            activeItem={remoteOptions.find((option) => option.value === remoteOption)}
          />
        </FormGroup>
        <FormGroup label="Duration">
          <Select
            items={durationOptions}
            onItemSelect={handleDurationChange}
            fill={true}
            buttonProps={{ large: true }}
            activeItem={durationOptions.find((option) => option.value === duration)}
          />
        </FormGroup>
        <Button fill={true} text="Update" large={true} disabled={disabled} onClick={handleSubmit} />
      </Card>
    </Drawer>
  );
}
